import moment from 'moment-timezone'

/**
 * Compliance quiet hours for manual SMS (TCPA plus the stricter state rules):
 * no business-initiated texts between 8:00 PM and 8:00 AM on the practice
 * location's clock. The workflow engine enforces its own copy of this rule
 * server-side; these helpers gate the manual send paths (inbox composer,
 * bulk send) client-side.
 */
export const QUIET_HOURS_START_HOUR = 20
export const QUIET_HOURS_END_HOUR = 8

/** The current moment on the practice clock: the IANA zone when moment knows
 *  it, otherwise the viewer's local clock (same guard as date.utils). */
function practiceNow(timezone: string | null | undefined, now: Date): moment.Moment {
  const zone = timezone && moment.tz.zone(timezone) ? timezone : undefined
  return zone ? moment.tz(now, zone) : moment(now)
}

/** True when `now` is inside quiet hours (>= 8:00 PM or < 8:00 AM) at the practice. */
export function isQuietHours(timezone?: string | null, now: Date = new Date()): boolean {
  const hour = practiceNow(timezone, now).hour()
  return hour >= QUIET_HOURS_START_HOUR || hour < QUIET_HOURS_END_HOUR
}

/**
 * The practice-local wall-clock time for quiet-hours copy, e.g. "9:14 PM".
 * Appends the zone abbreviation ("9:14 PM EDT") when the practice's offset
 * differs from the viewer's, matching the date.utils convention.
 */
export function practiceLocalTimeLabel(timezone?: string | null, now: Date = new Date()): string {
  const practice = practiceNow(timezone, now)
  const differs = practice.utcOffset() !== moment(now).utcOffset()
  return practice.format(differs ? `h:mm A z` : `h:mm A`)
}
